import { useMemo, useEffect, useState, useRef, useCallback } from "react";
import { Line } from "react-chartjs-2";
import { gSyncLogItem } from "../../interfaces";
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, Filler } from "chart.js";
import debounce from "lodash/debounce";

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, Filler);

export interface ChartFilterState {
    visibleSids: Set<string>;
    isMultiOperator: boolean;
}

type ChartSyncLogBarProps = {
    data: gSyncLogItem[];
    onFilterChange?: (filterState: ChartFilterState) => void;
};

// Color palette for multi-operator view
const OPERATOR_COLORS = [
    { bg: "rgba(0, 198, 175, 0.15)", border: "rgba(0, 198, 175, 1)" },
    { bg: "rgba(0, 120, 212, 0.15)", border: "rgba(0, 120, 212, 1)" },
    { bg: "rgba(156, 39, 176, 0.15)", border: "rgba(156, 39, 176, 1)" },
    { bg: "rgba(255, 152, 0, 0.15)", border: "rgba(255, 152, 0, 1)" },
    { bg: "rgba(76, 175, 80, 0.15)", border: "rgba(76, 175, 80, 1)" },
    { bg: "rgba(233, 30, 99, 0.15)", border: "rgba(233, 30, 99, 1)" },
    { bg: "rgba(63, 81, 181, 0.15)", border: "rgba(63, 81, 181, 1)" },
    { bg: "rgba(121, 85, 72, 0.15)", border: "rgba(121, 85, 72, 1)" },
];

const ChartSyncLogBar = ({ data, onFilterChange }: ChartSyncLogBarProps) => {
    const [dimensions, setDimensions] = useState({ width: window.innerWidth, height: 400 });
    const [dataState, setDataState] = useState<any>(data);
    const [hiddenDatasets, setHiddenDatasets] = useState<Set<number>>(new Set());
    const chartRef = useRef<any>(null);


    useEffect(() => {

        if (data?.length !== dataState?.length  || dataState.length == 0 ) {
            import.meta.env.DEV && console.log("‹new_data›", data?.length, "entries", dataState?.length, "entries");
            setDataState(data);
        }
    }, [data]);

    useEffect(() => {
        const handleResize = debounce(() => {
            setDimensions(prev => ({ ...prev, width: window.innerWidth }));
            if (chartRef.current) chartRef.current.resize(); // force chart to resize
        }, 100);

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            handleResize.cancel();
        };
    }, []);

    const { chartData, maxValue, isMultiOperator, uniqueSids } = useMemo(() => {
        import.meta.env.DEV && console.log("Processing chart data with", data.length, "entries");

        // detect unique operators (sids)
        const uniqueSids = [...new Set(data.map(item => item.sid).filter(Boolean))] as string[];
        const isMultiOp = uniqueSids.length > 1;

        if (!isMultiOp) {
            // Single operator mode - original behavior
            const dateCounts: { [date: string]: number } = {};
            const datePages:  { [date: string]: number } = {};

            data.forEach(item => {
                const date = new Date(item.stamp).toISOString().slice(0, 10);
                dateCounts[date] = (dateCounts[date] || 0) + 1;
                datePages[date]  = (datePages[date] || 0) + (item.page_count || 0);
            });

            const labels = Object.keys(dateCounts).sort();
            const counts = labels.map(date => dateCounts[date]);
            const pages  = labels.map(date => datePages[date]);

            const maxCount = Math.max(...counts, 0);
            const maxPages = Math.max(...pages, 0);
            const calculatedMaxValue = Math.max(maxCount, maxPages);
            const roundedMaxValue    = Math.ceil(calculatedMaxValue * 1.1);

            return {
                chartData: {
                    labels: labels.map(date => {
                        const d = new Date(date);
                        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }),
                    datasets: [
                        {
                            label: "Processed Files",
                            data: counts,
                            backgroundColor: "rgba(0, 198, 175, 0.1)",
                            borderColor: "rgba(0, 198, 175, 1)",
                            borderWidth: 2.5,
                            fill: true,
                            tension: 0.4,
                            pointRadius: 4,
                            pointHoverRadius: 6,
                            pointBackgroundColor: "rgba(0, 198, 175, 1)",
                            pointBorderColor: "#fff",
                            pointBorderWidth: 2,
                            pointHoverBackgroundColor: "#fff",
                            pointHoverBorderColor: "rgba(0, 198, 175, 1)",
                            pointHoverBorderWidth: 2,
                            yAxisID: 'y'
                        },
                        {
                            label: "Processed Pages",
                            data: pages,
                            backgroundColor: "rgba(0, 120, 212, 0.1)",
                            borderColor: "rgba(0, 120, 212, 1)",
                            borderWidth: 2.5,
                            fill: true,
                            tension: 0.4,
                            pointRadius: 4,
                            pointHoverRadius: 6,
                            pointBackgroundColor: "rgba(0, 120, 212, 1)",
                            pointBorderColor: "#fff",
                            pointBorderWidth: 2,
                            pointHoverBackgroundColor: "#fff",
                            pointHoverBorderColor: "rgba(0, 120, 212, 1)",
                            pointHoverBorderWidth: 2,
                            yAxisID: 'y1'
                        }
                    ]
                },
                maxValue: roundedMaxValue,
                isMultiOperator: false,
                uniqueSids
            };
        }

        // multi-operator mode - aggregate by operator and date
        const operatorDateFiles: { [sid: string]: { [date: string]: number } } = {};
        const operatorDatePages: { [sid: string]: { [date: string]: number } } = {};
        const sidToGroupname: { [sid: string]: string } = {};
        const allDates = new Set<string>();

        data.forEach(item => {
            const sid = item.sid || 'Unknown';
            const date = new Date(item.stamp).toISOString().slice(0, 10);
            allDates.add(date);

            // map sid to groupname (use first encountered groupname for each sid)
            if (!sidToGroupname[sid] && item.groupname) sidToGroupname[sid] = item.groupname;


            if (!operatorDateFiles[sid]) operatorDateFiles[sid] = {};
            if (!operatorDatePages[sid]) operatorDatePages[sid] = {};

            operatorDateFiles[sid][date] = (operatorDateFiles[sid][date] || 0) + 1;
            operatorDatePages[sid][date] = (operatorDatePages[sid][date] || 0) + (item.page_count || 0);
        });

        const sortedDates = [...allDates].sort();
        const formattedLabels = sortedDates.map(date => {
            const d = new Date(date);
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        });

        // create datasets for each operator (files only in multi-op for clarity)
        const datasets = uniqueSids.map((sid, index) => {
            const colorSet     = OPERATOR_COLORS[index % OPERATOR_COLORS.length];
            const filesData    = sortedDates.map(date => operatorDateFiles[sid]?.[date] || 0);
            const displayLabel = sidToGroupname[sid] || sid;

            return {
                label: displayLabel,
                data: filesData,
                backgroundColor: colorSet.bg,
                borderColor: colorSet.border,
                borderWidth: 2,
                fill: true,
                tension: 0.4,
                pointRadius: 3,
                pointHoverRadius: 5,
                pointBackgroundColor: colorSet.border,
                pointBorderColor: "#fff",
                pointBorderWidth: 1.5,
                pointHoverBackgroundColor: "#fff",
                pointHoverBorderColor: colorSet.border,
                pointHoverBorderWidth: 2,
                yAxisID: 'y'
            };
        });

        // Calculate max value for scale
        let maxVal = 0;
        sortedDates.forEach(date => {
            let dateTotal = 0;
            uniqueSids.forEach(sid => { dateTotal += operatorDateFiles[sid]?.[date] || 0 });
            maxVal = Math.max(maxVal, dateTotal);
        });

        return {
            chartData: {
                labels: formattedLabels,
                datasets
            },
            maxValue: Math.ceil(maxVal * 1.1),
            isMultiOperator: true,
            uniqueSids
        };
    }, [dataState]);

    // notify parent when filter changes (multi-operator mode only)
    useEffect(() => {
        if (onFilterChange && isMultiOperator && uniqueSids) {
            const visibleSids = new Set<string>( uniqueSids.filter((_: string, index: number) => !hiddenDatasets.has(index)) );
            onFilterChange({ visibleSids, isMultiOperator: true });
        }
    }, [hiddenDatasets, isMultiOperator, uniqueSids]);

    // reset hidden datasets when data changes significantly
    useEffect(() => {
        setHiddenDatasets(new Set());
    }, [uniqueSids?.length]);

    // custom legend click handler
    const handleLegendClick = useCallback((e: any, legendItem: any, legend: any) => {
        const index = legendItem.datasetIndex;
        const chart = legend.chart;

        // toggle dataset visibility (default Chart.js behavior)
        if (chart.isDatasetVisible(index)) {
            chart.hide(index);
        } else {
            chart.show(index);
        }

        // update our tracking state
        setHiddenDatasets(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    }, []);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index' as const,
            intersect: false,
        },
        scales: {
            x: {
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.05)',
                    drawBorder: false,
                },
                ticks: {
                    font: { size: 11, family: "'Raleway', 'Urbanist', system-ui, -apple-system, sans-serif" },
                    color: '#605e5c',
                }
            },
            y: {
                type: 'linear' as const,
                display: true,
                position: 'left' as const,
                min: 0,
                max: maxValue,
                stacked: isMultiOperator,
                grid: { display: true, color: 'rgba(0, 0, 0, 0.05)', drawBorder: false },
                ticks: {
                    font: { size: 11, family: "'Raleway', 'Urbanist', system-ui, -apple-system, sans-serif" },
                    color: '#605e5c',
                    callback: function(value: any) {
                        return Math.round(value);
                    }
                },
                title: {
                    display: true,
                    text: isMultiOperator ? 'Files Processed' : 'Documents',
                    color: 'rgba(0, 198, 175, 1)',
                    font: { size: 12, weight: 600, family: "'Urbanist', system-ui, -apple-system, sans-serif" }
                }
            },
            y1: {
                type: 'linear' as const,
                display: !isMultiOperator,
                position: 'right' as const,
                min: 0,
                max: maxValue,
                grid: { drawOnChartArea: false, drawBorder: false },
                ticks: {
                    font: { size: 11, family: "'Raleway', 'Urbanist', system-ui, -apple-system, sans-serif" },
                    color: '#605e5c',
                    callback: function(value: any) {
                        return Math.round(value);
                    }
                },
                title: {
                    display: true,
                    text: 'Pages',
                    color: 'rgba(0, 120, 212, 1)',
                    font: { size: 12, weight: 600, family: "'Raleway', 'Urbanist', system-ui, -apple-system, sans-serif" }
                }
            }
        },
        plugins: {
            legend: {
                position: 'top' as const,
                align: 'end' as const,
                onClick: isMultiOperator ? handleLegendClick : undefined,
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 15,
                    font: { size: 12, family: "'Raleway', 'Urbanist', system-ui, -apple-system, sans-serif", weight: 500 },
                    color: '#323130',
                }
            },
            title: {
                display: true,
                text: isMultiOperator ? 'Document Processing by Operator' : 'Document Handling Over Time',
                color: '#323130',
                font: { size: 16, weight: 600, family: "'Raleway', 'Urbanist', system-ui, -apple-system, sans-serif" },
                padding: { top: 10, bottom: 20 }
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#323130',
                bodyColor: '#605e5c',
                borderColor: '#e1e4e8',
                borderWidth: 1,
                padding: 12,
                boxPadding: 6,
                usePointStyle: true,
                titleFont: { size: 13, weight: 600, family: "'Raleway', 'Urbanist', system-ui, -apple-system, sans-serif" },
                bodyFont: { size: 12, family: "'Raleway', 'Urbanist', system-ui, -apple-system, sans-serif" },
                callbacks: {
                    label: function(context: any) {
                        let label = context.dataset.label || '';
                        if (label) label += ': ';

                        label += context.parsed.y;
                        return label;
                    }
                }
            }
        },
    }), [maxValue, isMultiOperator, handleLegendClick]);

    return chartData.labels.length ? (
        <div style={{ width: '100%', height: '400px', padding: '20px', background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)' }}>
            <Line ref={chartRef} data={chartData} options={options} />
        </div>
    ) : null;
};
export default ChartSyncLogBar;
